import React, { useState } from "react";
import { Autocomplete, TextField, Box, Chip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

interface VariableReferenceInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  availableVariables: string[];
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  // Scope/node_path support
  scopeLevel?: number; // 0=current, 1=parent (..), 2=grandparent (.., ..), etc.
  onScopeChange?: (level: number) => void;
  showScopeSelector?: boolean;
  defaultScopeLevel?: number;
}

/**
 * Flexible input for variable references that supports:
 * - Autocomplete suggestions from available variables
 * - Free-form text input for .status suffix, expressions, etc.
 * - Visual warning when value doesn't match a known variable
 * - Scope selector for node_path (parent, grandparent, etc.)
 */
const VariableReferenceInput: React.FC<VariableReferenceInputProps> = ({
  label,
  value,
  onChange,
  availableVariables,
  helperText,
  disabled = false,
  placeholder = "Type or select a variable",
  scopeLevel = 1,
  onScopeChange,
  showScopeSelector = false,
  defaultScopeLevel = 1,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [currentScope, setCurrentScope] = useState(scopeLevel || defaultScopeLevel);

  // Check if current value is a known variable (or variable.status)
  const baseVar = value.split(".")[0];
  const isKnownVariable = availableVariables.includes(baseVar);
  const hasStatusSuffix = value.endsWith(".status");
  const isValidReference = isKnownVariable || value === "" || hasStatusSuffix;

  const handleScopeChange = (newLevel: number) => {
    setCurrentScope(newLevel);
    if (onScopeChange) {
      onScopeChange(newLevel);
    }
  };

  const getScopeLabel = (level: number): string => {
    switch (level) {
      case 0:
        return "Current scope";
      case 1:
        return "Parent (..)";
      case 2:
        return "Grandparent (.., ..)";
      case 3:
        return "Great-grandparent (.., .., ..)";
      case 4:
        return "4 levels up";
      default:
        return `${level} levels up`;
    }
  };

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={availableVariables}
        value={value}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(_, newValue) => {
          if (newValue) {
            onChange(newValue);
          }
        }}
        onBlur={() => {
          // Commit the typed value on blur
          if (inputValue !== value) {
            onChange(inputValue);
          }
        }}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            placeholder={placeholder}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {!isValidReference && value && (
                    <WarningIcon
                      sx={{
                        color: "warning.main",
                        fontSize: "1.2rem",
                        mr: 1,
                      }}
                      titleAccess="Variable not declared in graph"
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Chip
              label={option}
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: 20,
                backgroundColor: "#E0E7FF",
              }}
            />
          </Box>
        )}
      />

      {/* Scope Selector */}
      {showScopeSelector && (
        <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={disabled}>
          <InputLabel>Variable Scope</InputLabel>
          <Select
            value={currentScope}
            onChange={(e) => handleScopeChange(Number(e.target.value))}
            label="Variable Scope"
          >
            <MenuItem value={0}>Current scope</MenuItem>
            <MenuItem value={1}>Parent (.. ↑1)</MenuItem>
            <MenuItem value={2}>Grandparent (.. .. ↑2)</MenuItem>
            <MenuItem value={3}>Great-grandparent (.. .. .. ↑3)</MenuItem>
            <MenuItem value={4}>4 levels up</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Warning for unknown variables */}
      {!isValidReference && value && (
        <Box sx={{ mt: 0.5, fontSize: "0.7rem", color: "warning.main" }}>
          ⚠️ Variable "{baseVar}" not declared in graph
        </Box>
      )}

      {/* Scope helper text */}
      {showScopeSelector && (
        <Box sx={{ mt: 0.5, fontSize: "0.7rem", color: "text.secondary", fontStyle: "italic" }}>
          Will resolve from {getScopeLabel(currentScope).toLowerCase()}
        </Box>
      )}
    </Box>
  );
};

export default VariableReferenceInput;
